import {
  Box,
  Paper,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ChatIcon from '@mui/icons-material/Chat';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useTranslation } from 'react-i18next';
import type { NotificationSettings } from '@/types';

interface NotificationSettingsFormProps {
  settings: NotificationSettings;
  onChange: (settings: NotificationSettings) => void;
}

export function NotificationSettingsForm({ settings, onChange }: NotificationSettingsFormProps) {
  const { t } = useTranslation('settings');

  const handleChange = (field: keyof NotificationSettings, value: boolean | 'ready_to_invoice' | 'work_completed') => {
    onChange({ ...settings, [field]: value });
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('notificationChannels')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmailIcon color="action" />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.emailNotifications}
                  onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('emailNotifications')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('emailNotificationsDesc')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, flex: 1 }}
            />
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SmsIcon color="action" />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smsNotifications}
                  onChange={(e) => handleChange('smsNotifications', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('smsNotifications')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('smsNotificationsDesc')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, flex: 1 }}
            />
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <NotificationsIcon color="action" />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.pushNotifications}
                  onChange={(e) => handleChange('pushNotifications', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('pushNotifications')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('pushNotificationsDesc')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, flex: 1 }}
            />
          </Box>
        </Box>
      </Paper>

      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('alertTypes')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarTodayIcon color="action" />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.newBookingAlert}
                  onChange={(e) => handleChange('newBookingAlert', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('newBookingAlert')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('newBookingAlertDesc')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, flex: 1 }}
            />
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ThumbUpIcon color="action" />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.approvalReceivedAlert}
                  onChange={(e) => handleChange('approvalReceivedAlert', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('approvalReceivedAlert')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('approvalReceivedAlertDesc')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, flex: 1 }}
            />
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ChatIcon color="action" />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.customerMessageAlert}
                  onChange={(e) => handleChange('customerMessageAlert', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('customerMessageAlert')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('customerMessageAlertDesc')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, flex: 1 }}
            />
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SummarizeIcon color="action" />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.dailySummaryEmail}
                  onChange={(e) => handleChange('dailySummaryEmail', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('dailySummaryEmail')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('dailySummaryEmailDesc')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, flex: 1 }}
            />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('customerNotifications')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DirectionsCarIcon color="action" />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.vehicleReadyCustomerEmail ?? false}
                  onChange={(e) => handleChange('vehicleReadyCustomerEmail', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('vehicleReadyCustomerEmail')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('vehicleReadyCustomerEmailDesc')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, flex: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WhatsAppIcon color="action" />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.vehicleReadyCustomerWhatsApp ?? false}
                  onChange={(e) => handleChange('vehicleReadyCustomerWhatsApp', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('vehicleReadyCustomerWhatsApp')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('vehicleReadyCustomerWhatsAppDesc')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, flex: 1 }}
            />
          </Box>
          <FormControl
            size="small"
            sx={{ ml: 5, maxWidth: 360 }}
            disabled={!((settings.vehicleReadyCustomerEmail ?? false) || (settings.vehicleReadyCustomerWhatsApp ?? false))}
          >
            <InputLabel id="vehicle-ready-trigger-label">{t('vehicleReadyTrigger')}</InputLabel>
            <Select
              labelId="vehicle-ready-trigger-label"
              label={t('vehicleReadyTrigger')}
              value={settings.vehicleReadyTrigger ?? 'ready_to_invoice'}
              onChange={(e) => handleChange('vehicleReadyTrigger', e.target.value as 'ready_to_invoice' | 'work_completed')}
            >
              <MenuItem value="ready_to_invoice">{t('vehicleReadyTriggerReadyToInvoice')}</MenuItem>
              <MenuItem value="work_completed">{t('vehicleReadyTriggerWorkCompleted')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('staffTasks')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <AssignmentIcon color="action" />
          <FormControlLabel
            control={
              <Switch
                checked={settings.quoteDecisionTaskEnabled ?? false}
                onChange={(e) => handleChange('quoteDecisionTaskEnabled', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {t('quoteDecisionTaskEnabled')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('quoteDecisionTaskEnabledDesc')}
                </Typography>
              </Box>
            }
            sx={{ m: 0, flex: 1 }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
